/**
 * The buyer's approximate area — for "Närmast mig", the distance chips and the
 * town + distance on the result cards (P25009-425).
 *
 * Privacy (BR-6, AC-4): only an AREA is ever kept or sent. A position is
 * rounded to two decimals (about a kilometre) before it reaches the URL, this
 * browser's storage or the API — never the browser's exact fix, never an
 * address. Distances shown from it are approximate, and say so.
 *
 * Where the area comes from, first match wins:
 *   1. the search URL's `origin` (the search being looked at),
 *   2. this browser's one-tap "Använd min plats" (wheelplace.buyerArea),
 *   3. the ort on the buyer's profile (protectedData.homeLocation).
 *
 * localStorage keys:
 *   wheelplace.buyerArea     { lat, lng, ts } — already rounded
 *   wheelplace.nearMePrompt  'dismissed' | 'answered' — the one-time
 *                            "Visa närmast?" question has been dealt with (BR-5)
 *
 * Nothing here runs on the server: every storage read is guarded, and a
 * missing or blocked storage simply means "no area known".
 */
import { types as sdkTypes } from './sdkLoader';
import { currentUserHomeLocation } from './homeLocation';
import geo from './geoAreaCore';

const { LatLng, LatLngBounds } = sdkTypes;

export const BUYER_AREA_KEY = 'wheelplace.buyerArea';
export const NEAR_ME_PROMPT_KEY = 'wheelplace.nearMePrompt';

/* The pure part — rounding, distance, the chips — is shared with the server
 * (bevakning distance alerts, listing positions): util/geoAreaCore.js. */
export const RADIUS_OPTIONS_KM = geo.RADIUS_OPTIONS_KM;
export const coarseArea = geo.coarseArea;
export const areaToOriginParam = geo.areaToOriginParam;
export const parseOriginParam = geo.parseOriginParam;
export const distanceKm = geo.distanceKm;
export const formatDistance = geo.formatDistance;
export const parseRadiusKm = geo.parseRadiusKm;
export const isWithinRadius = geo.isWithinRadius;

/** An area as the SDK's LatLng, for the `origin` query parameter. */
export const areaToLatLng = area => {
  const coarse = coarseArea(area);
  return coarse ? new LatLng(coarse.lat, coarse.lng) : null;
};

/**
 * The box around a circle, for the API's `bounds` filter — the API filters on
 * a box only, so the corners (up to 1.4 × the radius away) come back too and
 * are trimmed with isWithinRadius.
 */
export const boundsAroundKm = (center, radiusKm) => {
  const box = geo.boxAroundKm(center, radiusKm);
  return box
    ? new LatLngBounds(new LatLng(box.ne.lat, box.ne.lng), new LatLng(box.sw.lat, box.sw.lng))
    : null;
};

/* ---- storage ---- */

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
};

const readString = key => {
  try {
    return storage()?.getItem(key) || null;
  } catch {
    return null;
  }
};

const writeString = (key, value) => {
  try {
    const s = storage();
    if (!s) return;
    if (value == null) s.removeItem(key);
    else s.setItem(key, value);
  } catch {
    /* Private window or blocked storage: the area is simply not remembered. */
  }
};

/** The area this browser's "Använd min plats" found, or null. */
export const readStoredArea = () => {
  const raw = readString(BUYER_AREA_KEY);
  if (!raw) return null;
  try {
    return coarseArea(JSON.parse(raw));
  } catch {
    return null;
  }
};

/** Remember an area (rounded first — the exact fix is never stored). */
export const storeArea = area => {
  const coarse = coarseArea(area);
  if (coarse) writeString(BUYER_AREA_KEY, JSON.stringify({ ...coarse, ts: Date.now() }));
};

/** The ort on the buyer's profile, as an area, or null. */
export const profileArea = currentUser => coarseArea(currentUserHomeLocation(currentUser)?.origin);

/**
 * The buyer's area as far as the page knows it without asking, or null.
 * @param {Object} params
 * @param {*} [params.urlOrigin] the search URL's origin (LatLng or "lat,lng")
 * @param {Object} [params.currentUser]
 */
export const knownBuyerArea = ({ urlOrigin = null, currentUser = null } = {}) =>
  coarseArea(parseOriginParam(urlOrigin)) || readStoredArea() || profileArea(currentUser);

/* ---- the one-time question (BR-5) ---- */

/** True once the buyer has answered or dismissed "Visa närmast?". */
export const nearMePromptDone = () => !!readString(NEAR_ME_PROMPT_KEY);

/** @param {'answered'|'dismissed'} outcome */
export const markNearMePromptDone = outcome => writeString(NEAR_ME_PROMPT_KEY, outcome);

/* ---- asking the device ---- */

/** Why a location request gave no area, in the buyer's words. */
export const AREA_ERROR_TEXT = {
  unsupported: 'Din webbläsare kan inte dela plats. Du kan söka som vanligt eller ange din ort i din profil.',
  denied: 'Platsåtkomst nekades. Du kan söka som vanligt, tillåta plats i webbläsaren eller ange din ort i din profil.',
  unavailable: 'Vi kunde inte hämta din plats just nu. Du kan söka som vanligt.',
};

/**
 * Ask the browser for the buyer's position, once, at low accuracy, and return
 * it as an area. Rejects with { reason: 'unsupported' | 'denied' | 'unavailable' }.
 */
export const requestDeviceArea = ({ timeoutMs = 8000 } = {}) =>
  new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject({ reason: 'unsupported' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      position => {
        const area = coarseArea({
          lat: position?.coords?.latitude,
          lng: position?.coords?.longitude,
        });
        if (area) resolve(area);
        else reject({ reason: 'unavailable' });
      },
      err => reject({ reason: err?.code === 1 ? 'denied' : 'unavailable' }),
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 10 * 60 * 1000 }
    );
  });

/**
 * The buyer's area for an action that needs one ("Närmast mig", a distance
 * chip): what is already known (URL, this browser, the profile's ort), else
 * the device's position, remembered for next time. Rejects with { reason }
 * when there is none — the caller says so and the page goes on working.
 */
export const resolveBuyerArea = async ({ urlOrigin = null, currentUser = null } = {}) => {
  const known = knownBuyerArea({ urlOrigin, currentUser });
  if (known) return known;
  const device = await requestDeviceArea();
  storeArea(device);
  return device;
};
